import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../../../firebase/config';

const CLOUDINARY_URL = 'https://api.cloudinary.com/v1_1/dn9cspqmi/image/upload';
const UPLOAD_PRESET = 'ml_default';

export interface WeaponDocument {
  id: string;
  name: string;
  url: string;
  uploadedAt: Date;
  uploadedBy: string;
  fileType?: string;
  fileSize?: number;
}

/**
 * Upload document to Cloudinary
 */
export const uploadDocument = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);

  const response = await fetch(CLOUDINARY_URL, {
    method: 'POST',
    body: formData,
  });

  const data = await response.json();
  if (data.secure_url) {
    return data.secure_url;
  }
  throw new Error('Document upload failed');
};

/**
 * Add document to weapon
 */
export const addWeaponDocument = async (
  weaponId: string,
  companyId: string,
  file: File,
  documentName: string
): Promise<void> => {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated');
  }

  // Upload document to Cloudinary
  const documentUrl = await uploadDocument(file);

  // Get weapon document
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  const weaponSnap = await getDoc(weaponRef);

  if (!weaponSnap.exists()) {
    throw new Error('Weapon document not found');
  }

  const weaponData = weaponSnap.data();
  const documents = weaponData?.documents || [];

  const newDocument: WeaponDocument = {
    id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    name: documentName || file.name,
    url: documentUrl,
    uploadedAt: new Date(),
    uploadedBy: user.uid,
    fileType: file.type,
    fileSize: file.size,
  };

  // Update weapon document
  await updateDoc(weaponRef, {
    documents: [...documents, newDocument],
    updatedAt: serverTimestamp(),
  });
};

/**
 * Delete document from weapon
 */
export const deleteWeaponDocument = async (
  weaponId: string,
  companyId: string,
  documentId: string
): Promise<void> => {
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  const weaponSnap = await getDoc(weaponRef);

  if (!weaponSnap.exists()) {
    throw new Error('Weapon document not found');
  }

  const weaponData = weaponSnap.data();
  const documents = (weaponData?.documents || []).filter(
    (doc: WeaponDocument) => doc.id !== documentId
  );

  // Update weapon document
  await updateDoc(weaponRef, {
    documents: documents,
    updatedAt: serverTimestamp(),
  });
};

/**
 * Get weapon documents
 */
export const getWeaponDocuments = async (
  weaponId: string,
  companyId: string
): Promise<WeaponDocument[]> => {
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  const weaponSnap = await getDoc(weaponRef);

  if (!weaponSnap.exists()) {
    return [];
  }

  const weaponData = weaponSnap.data();
  const documents = weaponData?.documents || [];

  // Convert Firestore Timestamps to Date objects
  return documents.map((doc: any) => {
    let uploadedAt = doc.uploadedAt;
    if (uploadedAt && typeof uploadedAt === 'object') {
      if (typeof uploadedAt.toDate === 'function') {
        uploadedAt = uploadedAt.toDate();
      } else if (uploadedAt.seconds || uploadedAt._seconds) {
        const seconds = uploadedAt.seconds || uploadedAt._seconds || 0;
        const nanoseconds = uploadedAt.nanoseconds || uploadedAt._nanoseconds || 0;
        uploadedAt = new Date(seconds * 1000 + nanoseconds / 1000000);
      } else if (typeof uploadedAt === 'string') {
        uploadedAt = new Date(uploadedAt);
      }
    }
    return {
      ...doc,
      uploadedAt,
    };
  });
};

/**
 * Remove all documents from weapon (used when weapon is returned/deployed to another client)
 */
export const removeWeaponDocuments = async (
  weaponId: string,
  companyId: string
): Promise<void> => {
  const weaponRef = doc(db, 'weapons', companyId, 'companyWeapons', weaponId);
  await updateDoc(weaponRef, {
    documents: [],
    updatedAt: serverTimestamp(),
  });
};

